import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { withPartnerToken } from "utils/partnerSession.util";
import { dismissKeyboard } from "utils/navigation.util";
import "./MobileAppNav.scss";

const HomeIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="m3.5 10.5 8.5-7 8.5 7v9a1.5 1.5 0 0 1-1.5 1.5H5a1.5 1.5 0 0 1-1.5-1.5Z" />
    <path d="M9 21v-6.5h6V21" />
  </svg>
);

const BookingIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 16.5h14l-1.2-5.2a2 2 0 0 0-2-1.6H8.2a2 2 0 0 0-2 1.6Z" />
    <path d="M7.5 9.7 9 6.5h6l1.5 3.2M4 16.5V20m16-3.5V20" />
    <circle cx="7" cy="17" r="1.1" />
    <circle cx="17" cy="17" r="1.1" />
  </svg>
);

const HelpIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 13v-2a8 8 0 0 1 16 0v2" />
    <path d="M4 12.5h2.5v6H5.8A1.8 1.8 0 0 1 4 16.7Zm16 0h-2.5v6h.7a1.8 1.8 0 0 0 1.8-1.8ZM17.5 18.5c-.6 1.7-2.2 2.5-4.5 2.5" />
  </svg>
);

const SettingsIcon = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="3" />
    <path d="M19.2 13.4a7.7 7.7 0 0 0 0-2.8l2-1.5-2-3.4-2.4 1a8 8 0 0 0-2.4-1.4L14.1 2h-4.2l-.3 3.3a8 8 0 0 0-2.4 1.4l-2.4-1-2 3.4 2 1.5a7.7 7.7 0 0 0 0 2.8l-2 1.5 2 3.4 2.4-1a8 8 0 0 0 2.4 1.4l.3 3.3h4.2l.3-3.3a8 8 0 0 0 2.4-1.4l2.4 1 2-3.4Z" />
  </svg>
);

const navItems = [
  { path: "/", label: "appNavHome", fallback: "Home", icon: HomeIcon },
  {
    path: "/bookings",
    label: "appNavBookings",
    fallback: "Bookings",
    icon: BookingIcon,
  },
  { path: "/help", label: "appNavHelp", fallback: "Help", icon: HelpIcon },
  {
    path: "/settings",
    label: "appNavSettings",
    fallback: "Settings",
    icon: SettingsIcon,
  },
];

export default function MobileAppNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation("translation");
  const tabRoutes = ["/", "/bookings", "/help", "/settings"];
  const showNav = tabRoutes.includes(location.pathname);

  const navRef = useRef<HTMLElement | null>(null);
  const lastLefts = useRef<number[] | null>(null);
  /* Where the pill sits: the active tab's offset and width. */
  const [pill, setPill] = useState<{ x: number; w: number } | null>(null);
  /* No slide for the very first placement, only for moves after it. */
  const [placed, setPlaced] = useState(false);

  /* A tab switch animates like a native tab bar: the pill slides to the
     tapped tab and the icons glide to their new places, rather than both
     snapping. The tabs still re-lay out in one step (the open one is wider);
     each is then drawn back where it was and released to where it now is —
     transform only, so the motion runs on the compositor and costs no
     layout per frame (which is what made the old flex-grow transition
     stutter in the webview). */
  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) {
      /* Off the tab screens the bar is gone; coming back it is placed
         afresh, not slid in from wherever it was left. */
      lastLefts.current = null;
      setPill(null);
      setPlaced(false);
      return;
    }
    const buttons = Array.from(nav.querySelectorAll<HTMLElement>("button"));
    const lefts = buttons.map((b) => b.offsetLeft);
    const reduce = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const before = lastLefts.current;
    if (before && !reduce) {
      buttons.forEach((button, i) => {
        const dx = (before[i] ?? lefts[i]) - lefts[i];
        if (!dx) return;
        const icon = button.querySelector<HTMLElement>(".mobile-app-nav__icon");
        if (!icon) return;
        icon.style.transition = "none";
        icon.style.transform = `translateX(${dx}px)`;
        // Commit the start position before releasing it.
        void icon.offsetWidth;
        icon.style.transition = "";
        icon.style.transform = "";
      });
    }
    lastLefts.current = lefts;

    const active = nav.querySelector<HTMLElement>("button.is-active");
    setPill(active ? { x: active.offsetLeft, w: active.offsetWidth } : null);
    if (!placed && active) {
      const id = window.requestAnimationFrame(() => setPlaced(true));
      return () => window.cancelAnimationFrame(id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, showNav]);

  /* The bar's width follows the screen (rotation, split view); the pill
     follows the bar, without sliding. */
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      const active = nav.querySelector<HTMLElement>("button.is-active");
      if (active) setPill({ x: active.offsetLeft, w: active.offsetWidth });
      lastLefts.current = Array.from(
        nav.querySelectorAll<HTMLElement>("button")
      ).map((b) => b.offsetLeft);
    });
    observer.observe(nav);
    return () => observer.disconnect();
  }, [showNav]);

  /* All four tab screens are lazy chunks, so the first tap on each one used
     to wait on a network round trip before anything could render — a second
     or two on a phone, during which the bar looked like it had not responded.
     They are fetched once, shortly after this bar mounts, so every tab after
     that is already in memory when it is tapped.

     The specifiers must match routes/index.tsx exactly or webpack treats
     them as different chunks and this warms nothing. A plain timer rather
     than requestIdleCallback: idle callbacks are paint-driven and never fire
     in a backgrounded webview, which is precisely when there is time spare.
     Failures are swallowed — this is an optimisation, and the real
     navigation will surface any genuine load error. */
  useEffect(() => {
    const warm = window.setTimeout(() => {
      void import("pages/Services").catch(() => {});
      void import("pages/Bookings").catch(() => {});
      void import("pages/Help").catch(() => {});
      void import("pages/Settings").catch(() => {});
    }, 1200);
    return () => window.clearTimeout(warm);
  }, []);

  if (!showNav) return null;

  return (
    <nav
      ref={navRef}
      className="mobile-app-nav"
      aria-label={String(t("appNav", "Main navigation"))}
    >
      {pill && (
        <span
          className={`mobile-app-nav__pill${placed ? " is-placed" : ""}`}
          aria-hidden="true"
          style={{ width: pill.w, transform: `translateX(${pill.x}px)` }}
        />
      )}
      {navItems.map((item) => {
        const active = location.pathname === item.path;
        const Icon = item.icon;
        return (
          <button
            key={item.path}
            type="button"
            className={active ? "is-active" : ""}
            aria-current={active ? "page" : undefined}
            /* The label is no longer drawn, so it has to be announced.
               Without this the tab reads as an unnamed button. */
            aria-label={String(t(item.label, item.fallback))}
            title={String(t(item.label, item.fallback))}
            /* A tab switch swaps the screen in place, as a native tab bar
               does: replace, never push, and nothing for the open tab.
               Pushing added a history entry per tap — 24 quick taps meant
               24 presses of the phone's back button before it left. */
            onClick={() => {
              /* Keyboard down first, so the next screen is laid out at
                 full height rather than in the space above it. */
              dismissKeyboard();
              if (active) return;
              navigate(withPartnerToken(item.path), { replace: true });
            }}
          >
            <span className="mobile-app-nav__icon">
              <Icon />
            </span>
            {/* Only the current tab shows its name. The other three are
                named by aria-label above, so nothing is lost to a reader. */}
            {active && (
              <span className="mobile-app-nav__label">
                {t(item.label, item.fallback)}
              </span>
            )}
          </button>
        );
      })}
    </nav>
  );
}
